import React, { useState, useEffect } from "react";
import axios from "axios";

const NotificationCenterTool = ({ platformUrl, backEndUrl }) => {
    const [assignments, setAssignments] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userEmail, setUserEmail] = useState("");
    const [mobilePhone, setMobilePhone] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        const uuid = params.get("uuid");

        if (!token || !uuid) {
            setError("Missing access token or UUID. Please log in.");
            setLoading(false);
            return;
        }

        const fetchEmailAndAssignments = async () => {
            try {
                // Step 1: Get the user's email address
                const userResponse = await axios.get(
                    `${platformUrl}/learn/api/public/v1/users/uuid:${uuid}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                const email = userResponse.data.contact.email || "No Email Available";
                setUserEmail(email);

                const mobilePhone = userResponse.data.contact.mobilePhone || "No Mobile Phone Available";
                setMobilePhone(mobilePhone);

                // Step 2: Get courses for the user
                const coursesResponse = await axios.get(
                    `${platformUrl}/learn/api/public/v1/users/uuid:${uuid}/courses`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                // Step 3: Filter courses where courseRoleId is Student and availability is Yes
                const studentCourses = coursesResponse.data.results.filter(
                    (course) =>
                        course.availability?.available === "Yes" &&
                        course.courseRoleId === "Student"
                );

                // Step 4: Fetch course details and grade columns
                const currentDate = new Date();
                const oneWeekFromNow = new Date();
                oneWeekFromNow.setDate(currentDate.getDate() + 7);

                const assignmentsPromises = studentCourses.map(async (course) => {
                    // Fetch course details
                    const courseDetailsResponse = await axios.get(
                        `${platformUrl}/learn/api/public/v1/courses/${course.courseId}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );
                    const courseDetails = courseDetailsResponse.data;

                    // Fetch grade columns
                    const gradeColumnsResponse = await axios.get(
                        `${platformUrl}/learn/api/public/v1/courses/${course.courseId}/gradebook/columns`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );

                    // Filter grade columns for assignments due in the next week
                    const upcomingAssignments = gradeColumnsResponse.data.results
                        .filter((column) => column.grading?.due) // Ensure grading due date exists
                        .filter((column) => {
                            const dueDate = new Date(column.grading.due);
                            return dueDate >= currentDate && dueDate <= oneWeekFromNow;
                        })
                        .map((column) => ({
                            courseId: courseDetails.courseId,
                            courseName: courseDetails.name || "No Course Name Available",
                            assignmentName: column.name,
                            dueDate: new Date(column.grading.due).toLocaleString(),
                        }));

                    return upcomingAssignments;
                });

                const allAssignments = (await Promise.all(assignmentsPromises)).flat();
                setAssignments(allAssignments);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchEmailAndAssignments();
    }, [platformUrl]);

    const sendNotification = async () => {
        if (assignments.length === 0) {
            alert("No assignments to send.");
            return;
        }

        const tableHtml = `
            <table border="1">
                <thead>
                    <tr>
                        <th>Course ID</th>
                        <th>Course Name</th>
                        <th>Assignment Name</th>
                        <th>Due Date</th>
                    </tr>
                </thead>
                <tbody>
                    ${assignments
            .map(
                (assignment) => `
                        <tr>
                            <td>${assignment.courseId}</td>
                            <td>${assignment.courseName}</td>
                            <td>${assignment.assignmentName}</td>
                            <td>${assignment.dueDate}</td>
                        </tr>
                    `
            )
            .join("")}
                </tbody>
            </table>
        `;

        try {
            const response = await axios.post(
                `${backEndUrl}/sendNotification?email=${encodeURIComponent(userEmail)}&mobilePhone=${encodeURIComponent(mobilePhone)}`,
                { html: tableHtml },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            alert("Notification sent successfully.");
        } catch (err) {
            console.error("Error sending notification:", err);
            alert("Failed to send notification. Please try again.");
        }
    };

    if (loading) {
        return <div className="bb-loading">Loading...</div>;
    }

    if (error) {
        return (
            <div className="bb-error">
                <h1>Error</h1>
                <p>{error}</p>
            </div>
        );
    }

    return (
        <div className="notification-center-tool-container">
            <h1>Notification Center</h1>
            {assignments.length === 0 ? (
                <p>No assignments with due dates in the next week.</p>
            ) : (
                <>
                    <table className="bb-table">
                        <thead>
                        <tr>
                            <th>Course ID</th>
                            <th>Course Name</th>
                            <th>Assignment Name</th>
                            <th>Due Date</th>
                        </tr>
                        </thead>
                        <tbody>
                        {assignments.map((assignment, index) => (
                            <tr key={index}>
                                <td>{assignment.courseId}</td>
                                <td>{assignment.courseName}</td>
                                <td>{assignment.assignmentName}</td>
                                <td>{assignment.dueDate}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <button className="send-notification-button" onClick={sendNotification}>
                        Send Notification
                    </button>
                </>
            )}
        </div>
    );
};

export default NotificationCenterTool;
